import Icon from "@Components/generic/icon";
import { Menu, MenuItem, useTheme } from "@mui/material";
import { AriaAttributes, MouseEventHandler, ReactElement, useId, useState } from "react";
import { DSIcon } from "../icons";

export interface IBybDropdownTriggerProps extends AriaAttributes {
  id: string;
  disabled: boolean;
  onClick: MouseEventHandler<HTMLElement>;
}

export interface IBybDropdownOption {
  value: string;
  label: string;
}

export interface IBybDropdown {
  value: string;
  ariaLabel: string;
  /** Spread these props onto the focusable trigger to preserve menu anchoring and accessibility. */
  renderTrigger: (props: IBybDropdownTriggerProps) => ReactElement;
  options: readonly IBybDropdownOption[];
  disabled?: boolean;
  onChange: (value: string) => void;
}

export const BybDropdown = ({ value, ariaLabel, options, renderTrigger, disabled = false, onChange }: IBybDropdown) => {
  const theme = useTheme();
  const menuId = useId();
  const triggerId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeMenu = () => setAnchorEl(null);

  return (
    <>
      {renderTrigger({
        id: triggerId,
        disabled,
        "aria-label": ariaLabel,
        "aria-haspopup": "menu",
        "aria-expanded": Boolean(anchorEl),
        "aria-controls": anchorEl ? menuId : undefined,
        onClick: event => {
          if (!disabled) setAnchorEl(event.currentTarget);
        },
      })}
      <Menu
        id={menuId}
        MenuListProps={{ "aria-labelledby": triggerId }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        transitionDuration={{ enter: theme.transitions.duration.shorter, exit: 0 }}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ elevation: 0, sx: { minWidth: anchorEl?.offsetWidth } }}
      >
        {options.map(option => (
          <MenuItem
            key={option.value}
            selected={value === option.value}
            disabled={disabled}
            onClick={() => {
              onChange(option.value);
              closeMenu();
            }}
          >
            {option.label}
            {value === option.value && <Icon dsIcon={DSIcon.CHECK} size={16} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
